import {
  Controller,
  Control,
  FieldValues,
  Path,
  ControllerProps,
} from "react-hook-form";
import {
  Button,
  Stack,
  Typography,
  FormHelperText,
  ButtonProps,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEffect } from "react";

export type FormFileInputProps<T extends FieldValues> = Omit<
  ControllerProps<T>,
  "render"
> & {
  label: string;
  control: Control<T>;
  name: Path<T>;
  buttonProps?: Partial<ButtonProps>;
  disabled?: boolean;
};

const FormFileInput = <T extends FieldValues>({
  name,
  control,
  label,
  rules,
  disabled,
  buttonProps = {},
  ...controllerProps
}: FormFileInputProps<T>) => {
  const isRequired = !!rules?.required;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      shouldUnregister={false}
      {...controllerProps}
      render={({ field: { onChange, value }, fieldState }) => (
        <Stack spacing={1}>
          {label && (
            <Typography fontWeight={600}>
              {label} {isRequired && "*"}
            </Typography>
          )}
          <Stack flexDirection={"row"} gap={2}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              disabled={disabled}
              startIcon={<UploadFileIcon />}
              {...buttonProps}
            >
              {value && value.length > 0 ? value[0].name : "Choose File"}
              <input
                type="file"
                hidden
                style={{
                  display: "none",
                }}
                disabled={disabled}
                onChange={(e) => {
                  const files = e.target.files;
                  if (files && files.length > 0) {
                    onChange(files);
                  } else {
                    onChange(null);
                  }
                }}
                value={undefined}
              />
            </Button>

            {value?.length > 0 && (
              <Button
                variant="text"
                color="error"
                startIcon={<DeleteIcon />}
                disabled={disabled}
                onClick={() => onChange(null)}
              >
                Remove
              </Button>
            )}
          </Stack>

          {fieldState.error && (
            <FormHelperText error>{fieldState.error.message}</FormHelperText>
          )}
        </Stack>
      )}
    />
  );
};

export default FormFileInput;
